import { Outlet } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { Footer } from './Footer'
import { EnvironmentBanner } from './EnvironmentBanner'
import { CriticalAlertsProvider } from './CriticalAlertsProvider'
import { CriticalAlertBar } from './CriticalAlertBar'
import { SoundBar } from './SoundBar'  // #337
import { CriticalPopupStack } from '@/components/notifications/CriticalPopupStack'
import { useUIStore } from '@/store/uiStore'
import { useLanguageSync } from '@/hooks/useLanguageSync'
import { cn } from '@/lib/utils'
import { DashboardMapAnchorProvider } from '@/contexts/DashboardMapAnchorContext'
import { PersistentDashboardMap } from '@/components/maps/PersistentDashboardMap'
import { RestorationProvider, useRestoration } from './RestorationProvider'

export function MainLayout() {
  return (
    <RestorationProvider>
      <MainLayoutInner />
    </RestorationProvider>
  )
}

function MainLayoutInner() {
  const { sidebarCollapsed, sidebarOpen, setSidebarOpen } = useUIStore()
  const { mainRef } = useRestoration()
  useLanguageSync()

  return (
    <DashboardMapAnchorProvider>
      {/* #294 redesign — the ONE critical alerts controller (open list, sound, tab title and icon badge);
          the alert bar, the popups and the map pins read it. */}
      <CriticalAlertsProvider>
        {/* PersistentDashboardMap renders once per session — outside <Outlet> so
            it never unmounts on route changes. It renders either null or a React
            Portal into the anchor div that DashboardPage publishes. */}
        <PersistentDashboardMap />

        {/* #294 redesign — the critical popups: their own fixed column under the alert bar, on every page
            (not inside PeekCards, which is also mounted on /login). */}
        <CriticalPopupStack />

        <div className="relative flex h-[100dvh] w-full bg-background overflow-hidden">
          {/* Mobile overlay — fade transition */}
          <div
            className={cn(
              'fixed inset-0 z-40 bg-black/50 lg:hidden transition-opacity duration-300',
              sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            )}
            onClick={() => setSidebarOpen(false)}
          />

          <Sidebar />

          {/* Main content wrapper — fixed height so header sticks and only main scrolls */}
          <div
            className={cn(
              'flex flex-1 flex-col h-full w-full overflow-hidden',
              // Desktop: offset by sidebar width, synced transition with sidebar
              'lg:ml-64 lg:transition-[margin-left] lg:duration-300',
              sidebarCollapsed && 'lg:ml-16'
            )}
          >
            <EnvironmentBanner />
            <Header/>
            {/* #337 — the amber "alert sounds are off until you click" bar, on every page while the browser blocks sound */}
            <SoundBar />
            {/* #294 redesign — the slim alert bar under the header on every page while a critical alert is open */}
            <CriticalAlertBar />

            {/* Main content area - only this scrolls */}
            <main
              ref={mainRef}
              className="flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain flex flex-col min-h-0"
            >
              <div className="p-3 sm:p-4 md:p-6 lg:p-6 max-w-full min-w-0 flex-1">{/* #368 — 24 px from lg (was 32) */}
                <Outlet />
              </div>
              <Footer />
            </main>
          </div>
        </div>
      </CriticalAlertsProvider>
    </DashboardMapAnchorProvider>
  )
}
